import styled, { keyframes } from 'styled-components';

// Define the spinning animation
const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Define the fade-in animation for the text
const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Define the pulse animation
const pulseAnimation = keyframes`
  0%, 100% { 
    opacity: 1; 
    transform: scale(1); 
  }
  50% { 
    opacity: 0.5; 
    transform: scale(1.1); 
  }
`;

// Define the skeleton loading animation
const skeletonPulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

// Main container for the spinner
export const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${props => {
    if (props.$fullPage) return '100vh';
    if (props.$inline) return 'auto';
    return 'auto';
  }};
  padding: ${props => {
    if (props.$fullPage) return '0';
    if (props.$inline) return '10px';
    return '30px';
  }};
  background: ${props => {
    if (props.$fullPage || props.$overlay) return props.theme.colors.bg1;
    return 'transparent';
  }};
  position: ${props => props.$overlay ? 'fixed' : 'relative'};
  top: ${props => props.$overlay ? '0' : 'auto'};
  left: ${props => props.$overlay ? '0' : 'auto'};
  right: ${props => props.$overlay ? '0' : 'auto'};
  bottom: ${props => props.$overlay ? '0' : 'auto'};
  z-index: ${props => props.$overlay ? '1000' : 'auto'};
  backdrop-filter: ${props => props.$overlay ? 'blur(2px)' : 'none'};
  text-align: ${props => props.$center ? 'center' : 'left'};
`;

// The spinner element itself
export const Spinner = styled.div`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border: ${props => props.$thickness}px solid rgba(0, 0, 0, 0.1);
  border-top: ${props => props.$thickness}px solid ${props => 
    props.$color || props.theme.colors.primary
  };
  border-radius: 50%;
  animation: ${spinAnimation} ${props => props.$speed}s linear infinite;
`;

// Logo spinner with pulsing color effect (like a sensor)
export const VerifiedSpinner = styled.div`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 100%;
    height: 100%;
    color: ${({theme}) => theme.colors.error}; // Red color for loading logo
    animation: ${pulseAnimation} ${props => props.$speed}s ease-in-out infinite;
  }
`;

// Simple opacity-only pulse for inline elements
const opacityPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

// Inline logo spinner - compact version for buttons and small spaces
export const InlineVerifiedSpinner = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$size || 14}px;
  height: ${props => props.$size || 14}px;
  line-height: 1;
  
  svg {
    width: 100%;
    height: 100%;
    color: ${props => props.$color || 'currentColor'};
    animation: ${opacityPulse} ${props => props.$speed || 0.8}s ease-in-out infinite;
  }
`;

// Pulse loader
export const PulseLoader = styled.div`
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  background: ${({theme}) => theme.colors.primary};
  border-radius: 50%;
  animation: ${pulseAnimation} 1.5s ease-in-out infinite;
`;

// Skeleton container
export const SkeletonContainer = styled.div`
  width: 100%;
  max-width: 300px;
`;

// Skeleton bar
export const SkeletonBar = styled.div`
  height: 16px;
  background: ${props => props.theme.colors.bg3 || '#f0f0f0'};
  border-radius: 4px;
  margin-bottom: 8px;
  width: ${props => props.$width || '100%'};
  animation: ${skeletonPulse} 2s ease-in-out infinite;
  animation-delay: ${props => props.$delay || 0}s;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// The loading text
export const LoadingText = styled.p`
  margin-top: 20px;
  color: ${props => props.$textColor};
  font-size: ${props => props.$textSize}px;
  font-weight: ${props => props.$textWeight};
  animation: ${fadeIn} 0.5s ease-in-out;
  display: ${props => props.$showText ? 'block' : 'none'};
  opacity: 0.8;
  text-align: center;
`;